import { useContext, useMemo } from 'react';
import AppliedFiltersContext from '~/context/srp/AppliedFiltersContext';
import { useSavedSearches } from '~/context/srp/SavedSearchesContext';
import type { AppliedFilters, MakeType, ModelType } from '~/contracts/common';
import type { SavedSearch } from '~/contracts/srp';
import compareArrays from '~/utilities/srp/compareArrays';
import decodeCvnaId from '~/utilities/srp/cvnaId';

export type FilterValue = string | string[] | number | boolean;

type MakeOrModel = MakeType | ModelType;
type FlatMakes = Array<string | string[]>;

function isMakeOrModel(key: string, _: unknown): _ is MakeOrModel[] {
  return key === 'makes';
}

/**
 * sorts filter object for more accurate comparisons:
 * {"min": 15, "max": 100} => {"max": 100, "min": 15}
 */
function sortObjectKeys(obj: Record<string, any>): Record<string, any> {
  const sortedKeys = Object.keys(obj).sort();
  const sortedObj = {};

  for (const key of sortedKeys) {
    sortedObj[key] = obj[key];
  }

  return sortedObj;
}

/**
 * reduces the makes filter into a single array:
 * ["Honda", "Civic", "Trim_1", "Trim_2", "Toyota", "Camry"]
 */
function extractMakesModelsTrims(items: MakeOrModel[]): FlatMakes {
  return items.reduce((acc: FlatMakes, m: MakeOrModel) => {
    acc.push(m.name);

    if ('models' in m && m.models) {
      acc.push(...extractMakesModelsTrims(m.models));
    }

    if ('parentModels' in m && m.parentModels) {
      acc.push(...extractMakesModelsTrims(m.parentModels));
    }

    if ('trims' in m && m.trims) {
      acc.push(...m.trims);
    }

    return acc;
  }, []);
}

/**
 * Flattens all filters into an array that can be compared
 * Range filters are flattened with JSON.stringify
 * Makes is flattened using extractMakesModelsTrims
 */
function flattenFilters(filters: AppliedFilters): FilterValue[] {
  return Object.entries(filters).flatMap(([key, values]) => {
    if (isMakeOrModel(key, values)) {
      return extractMakesModelsTrims(values);
    }

    if (Array.isArray(values)) {
      return JSON.stringify([...values].sort());
    }

    if (typeof values === 'object') {
      return JSON.stringify(sortObjectKeys(values));
    }

    return JSON.stringify(values);
  });
}

// determines if any savedSearches' filters match the current filters
export function isCurrentSearchSaved(appliedFilters: AppliedFilters) {
  return ({ url }: SavedSearch): boolean => {
    const savedFilters = decodeCvnaId(url).filters ?? {};

    // flattening all values into a single array for comparing
    const appliedFiltersToCompare = flattenFilters(appliedFilters);
    const savedFiltersToCompare = flattenFilters(savedFilters);

    return compareArrays(appliedFiltersToCompare, savedFiltersToCompare);
  };
}

export function useCurrentSavedSearchId(): number | null {
  const { appliedFilters } = useContext(AppliedFiltersContext);
  const savedSearches = useSavedSearches();

  return useMemo(() => {
    const savedSearch = savedSearches.find(
      isCurrentSearchSaved(appliedFilters)
    );

    return savedSearch ? savedSearch.savedSearchId : null;
  }, [appliedFilters, savedSearches]);
}

export default function useSavedSearchApplied(): boolean {
  const { appliedFilters } = useContext(AppliedFiltersContext);
  const savedSearches = useSavedSearches();

  return useMemo(
    () => savedSearches.some(isCurrentSearchSaved(appliedFilters)),
    [appliedFilters, savedSearches]
  );
}
